<template>
  <div
    v-bind:class="{
      'd-flex': !vertical
    }"
  >
    <template v-for="(data, title, dataIndex) in filteredValues">
      <v-card
        flat
        class="mt-6 mb-6"
        style="width: 100%"
        v-bind:style="{
          background: data.cardColor ? data.cardColor : 'transparent'
        }"
        :key="title"
      >
        <v-card-title dense v-if="!data.noTitle" class="mb-n4 mt-n4">
          <v-btn class="ml-n6" icon @click="toggle(title)">
            <icon v-if="show[title]" scale="0.75" name="chevron-up" />
            <icon v-else scale="0.75" name="chevron-down" />
          </v-btn>
          <div class="text-h6" v-bind:style="{ color: data.titleColor }">
            {{ title }}
          </div></v-card-title
        ><v-expand-transition>
          <v-card
            flat
            v-show="show[title]"
            class="mb-2"
            style="width: 95%; height: 95%"
            v-bind:style="{
              background: data.cardSubColor ? data.cardSubColor : 'transparent'
            }"
          >
            <v-row
              v-for="([field, value], index) in Object.entries(data.fields)"
              :key="field"
              class="ml-2 mr-2"
              v-bind:class="{
                'mt-n6': index !== 0,
                'mb-n6': index !== Object.entries(data.fields).length - 1
              }"
            >
              <v-col cols="7">
                {{ field }}
              </v-col>
              <v-col align="right">
                {{ value }}

                <span v-if="data.bestFields && data.bestFields[field] == value">
                  <i
                    aria-hidden="true"
                    class="
                      v-icon
                      notranslate
                      material-icons
                      theme--light
                      starIcon
                    "
                    >star</i
                  ></span
                >
              </v-col>
              <v-col
                v-if="data.bisFields && data.bisFields[field]"
                align="right"
                class="bisvalue"
              >
                <div v-html="data.bisFields[field]"></div>
              </v-col>
            </v-row>

            <v-divider
              class="mt-2 mb-n3"
              v-if="
                vertical &&
                !data.noDivider &&
                data.cardColor == null &&
                dataIndex !== nbOfCategories - 1
              "
            />
          </v-card>
        </v-expand-transition>
      </v-card>
      <v-divider
        vertical
        class="mr-2"
        v-if="
          !vertical &&
          !data.noDivider &&
          data.cardColor == null &&
          dataIndex !== nbOfCategories - 1
        "
      />
    </template>
  </div>
</template>

<script>
import dataTools from '../../tools/data.js'

export default {
  name: 'DataPanel',
  props: {
    values: {
      type: Object,
      default: null
    },
    categories: {
      type: Object,
      default: {}
    },
    context: {
      type: String,
      default: ''
    },
    bisValues: {
      type: Object,
      default: null
    },
    bestValues: {
      type: Object,
      default: null
    },
    vertical: {
      type: Boolean,
      default: true
    }
  },
  mixins: [],
  data() {
    return {
      show: {}
    }
  },
  created() {
    for (const category of Object.keys(this.categories)) {
      //Without $set, the properties would not be reactive
      this.$set(this.show, category, true)
    }
  },
  computed: {
    nbOfCategories() {
      return Object.entries(this.categories).length
    },
    filteredValues() {
      const filteredValues = {}

      for (const [category, data] of Object.entries(this.categories)) {
        const fields = {}
        const bisFields = {}
        const bestFields = {}

        for (const field of data.fields) {
          const fieldName = field.fieldName
          const value = this.values[fieldName]
          const fieldType =
            field.fieldType != null
              ? field.fieldType
              : dataTools.getFieldTypeFromPath([fieldName], this.context)
          const fieldLabel = field.label
            ? field.label
            : dataTools.getFieldLabelFromPath([fieldName], this.context)

          let valueToDisplay = dataTools.convertSi2Display(fieldType, value)

          if (
            !isNaN(valueToDisplay) &&
            !isNaN(value) &&
            value != 0 &&
            parseFloat(valueToDisplay) == 0 &&
            fieldName.includes('Delta') &&
            (fieldType == 'distance' ||
              fieldType == 'angle' ||
              fieldType == 'coefficient')
          ) {
            // To avoid displaying 0 for very small variation amounts, and only for some very specific cases:
            // multiply by 1000 before type conversion, then divide after, then convert to scientific notation
            let fVal = parseFloat(valueToDisplay)
            if (fVal < 0.0001 && fVal > -0.0001) {
              fVal =
                parseFloat(
                  dataTools.convertSi2Display(fieldType, value * 1000)
                ) / 1000
              valueToDisplay = fVal.toExponential(1)
            }
          }

          valueToDisplay === '-0' ? (valueToDisplay = '0') : ''

          if (valueToDisplay === '') {
            valueToDisplay = 'N/A'
          }

          const fieldUnit = dataTools.getDefaultGuiUnit(fieldType)
          const fieldLabelDisplayed =
            fieldUnit !== '' ? `${fieldLabel} (${fieldUnit})` : fieldLabel
          fields[category] = {
            ...fields[category],
            [fieldLabelDisplayed]: valueToDisplay
          }

          if (this.bisValues && this.bisValues[fieldName]) {
            const bisValue = this.bisValues[fieldName]
            let bisValueToDisplay = dataTools.convertSi2Display(
              fieldType,
              bisValue
            )

            // Apply symbol and scientific notation for uncertainties values
            bisValueToDisplay =
              '&#177;' + parseFloat(bisValueToDisplay).toExponential(1)

            bisFields[category] = {
              ...bisFields[category],
              [fieldLabelDisplayed]: bisValueToDisplay
            }
          }

          if (this.bestValues && this.bestValues[fieldName]) {
            const bestValue = this.bestValues[fieldName]
            let bestValueToDisplay = dataTools.convertSi2Display(
              fieldType,
              bestValue
            )

            bestFields[category] = {
              ...bestFields[category],
              [fieldLabelDisplayed]: bestValueToDisplay
            }
          }
        }
        filteredValues[category] = {
          ...data,
          fields: fields[category]
        }

        if (bisFields[category]) {
          filteredValues[category].bisFields = bisFields[category]
        }
        if (bestFields[category]) {
          filteredValues[category].bestFields = bestFields[category]
        }
      }

      return filteredValues
    }
  },
  methods: {
    toggle(title) {
      this.show[title] = !this.show[title]
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css?family=Roboto');

.bisvalue {
  font-weight: lighter;
  font-style: italic;
  font-family: 'Roboto';
  color: #999999;
}

.starIcon {
  margin-right: -18px;
  margin-bottom: 2px;
  font-size: 14px;
  color: #5757ff;
}
</style>
